import { ErrorSharp } from "@mui/icons-material";
import { Button, FormControl, FormHelperText, Grid, InputLabel, OutlinedInput } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { ResultBoardStateSetter } from "@shared/mirage/core/contracts/ResultBoard";
import { ForgotPasswordRequest, ForgotPasswordResult } from "@shared/mirage/core/contracts/User";
import { IForgotPasswordService } from "@shared/mirage/services/IForgotPasswordService";
import validators from "@shared/mirage/validators";
import { useState } from "react";
import { Link } from "react-router-dom";

interface ForgotPasswordBoilerplateProps {
    service: IForgotPasswordService;
    backButtonRoute: string;
    setResultBoard: ResultBoardStateSetter;
    emailAddress: string;
    setEmailAddress: React.Dispatch<React.SetStateAction<string>>;
}

const ForgotPasswordBoilerplate = (props: ForgotPasswordBoilerplateProps) => {
    const [emailValidation, setEmailValidation] = useState({ error: "", passed: true });

    const handleEmailOnChange = (value: string) => {
        props.setEmailAddress(() => value);
        emailValidationHandler(value);
    };

    const emailValidationHandler = (value: string) => {
        setEmailValidation(() => {
            const validationResult = validators.EmailValidator(value);
            return {
                error: validationResult?.error ? validationResult?.error : "",
                passed: validationResult?.passed ? validationResult?.passed : false,
            };
        });
    };

    const sendOnClickHandler = async () => {
        if (emailValidation.error) {
            return;
        }
        props.setResultBoard((prevState) => ({ ...prevState, show: true, isLoading: true }));
        const forgotPasswordRequest: ForgotPasswordRequest = {
            email: props.emailAddress,
        };
        if (props.emailAddress) {
            const forgotPasswordResult: ForgotPasswordResult = await props.service.forgotPassword(forgotPasswordRequest);
            if (forgotPasswordResult.errorMsg?.length === 0) {
                props.setResultBoard((prevState) => ({ ...prevState, isLoading: false, success: true, message: "Your password reset email has been sent to: " }));
            } else {
                props.setResultBoard((prevState) => ({ ...prevState, isLoading: false, success: false, message: forgotPasswordResult?.errorMsg || "Something went wrong." }));
            }
        }
    };

    const getButtons = () => {
        return (
            <Grid container justifyContent="center" alignItems="center" sx={{ marginTop: "2rem" }}>
                <Grid item xs={3} justifyContent="center">
                    <Link to={props.backButtonRoute}>
                        <Button>Back</Button>
                    </Link>
                </Grid>
                <Grid item xs={3} textAlign="center">
                    <Button
                        variant="contained"
                        onClick={() => {
                            emailValidationHandler(props.emailAddress);
                            sendOnClickHandler();
                        }}
                    >
                        Send
                    </Button>
                </Grid>
            </Grid>
        );
    };

    const getForgotPasswordBoard = () => {
        return (
            <Box sx={{ paddingTop: "1rem" }}>
                <Stack spacing={4} justifyContent={"center"} alignItems={"center"}>
                    <FormControl variant="outlined" sx={{ width: "60%" }} key={`reset-password-email-key`} required={true}>
                        <InputLabel error={!emailValidation.passed} htmlFor={"reset-password-email-id"}>
                            Email Address
                        </InputLabel>
                        <OutlinedInput
                            defaultValue={props.emailAddress}
                            id={"reset-password-email-id"}
                            type={"email"}
                            label={"Email Address"}
                            error={!emailValidation.passed}
                            onChange={(event) => handleEmailOnChange(event.target.value)}
                            onBlur={(event) => emailValidationHandler(event.target.value)}
                        />
                        <FormHelperText error={!emailValidation.passed} sx={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
                            {!emailValidation.passed && <ErrorSharp sx={{ width: "0.7em", height: "0.7em" }} />}
                            {!emailValidation.passed && emailValidation.error}
                        </FormHelperText>
                    </FormControl>
                    {getButtons()}
                </Stack>
            </Box>
        );
    };

    return <>{getForgotPasswordBoard()}</>;
};

export default ForgotPasswordBoilerplate;
